import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const payload = new FormData(form);
      payload.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d96bflvpg5pqhofcun4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else if (serverMsg && serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError('提交验证失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51每日大赛',
    description: '51每日大赛是一个专注于网红明星塌房事件和娱乐八卦资讯的聚合平台，致力于为吃瓜群众提供最新、最快、最全的爆料内容。',
    url: 'https://www.51meiridasai.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '51每日大赛',
      url: 'https://www.51meiridasai.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51每日大赛是什么网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51每日大赛是一个专注于网红明星塌房事件和娱乐八卦资讯的聚合平台，致力于为吃瓜群众提供最新、最快、最全的爆料内容。我们每日更新，覆盖网红圈、娱乐圈、电竞圈等多个领域。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向51每日大赛投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过我们的爆料投稿页面提交线索，或发送邮件至 tips@51meiridasai.com。我们承诺对爆料人信息严格保密，并会在核实后第一时间发布。',
        },
      },
      {
        '@type': 'Question',
        name: '51每日大赛的内容来源可靠吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的内容团队通过多渠道交叉验证，确保每一条爆料的真实性。对于尚未完全证实的信息，我们会在文章中明确标注，让读者自行判断。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 51每日大赛 | 品牌介绍与联系我们"
        description="了解51每日大赛的品牌故事、平台口碑与联系方式。51每日大赛官网，网红明星塌房现场猛料实时追踪，每日更新吃瓜。"
        keywords="51每日大赛,关于我们,品牌介绍,联系我们,爆料投稿"
        canonicalUrl="https://www.51meiridasai.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20abstract%20composition%20with%20flowing%20warm%20golden%20and%20deep%20red%20waves%2C%20elegant%20artistic%20background%2C%20sophisticated%20texture%2C%20professional%20corporate%20aesthetic%2C%20warm%20atmosphere%2C%20minimal%20luxurious%20style&width=1800&height=500&seq=about-hero-brand-01&orientation=landscape"
            alt="关于51每日大赛"
            title="51每日大赛品牌介绍"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/60"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-white text-3xl md:text-5xl font-black">关于51每日大赛</h1>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="pb-14">
              {/* Brand Introduction */}
              <div className="flex flex-col lg:flex-row gap-8 mb-16">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-black text-foreground-900 mb-6">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                    <p>
                      <strong className="text-foreground-900">51每日大赛</strong>成立于2024年，是国内领先的娱乐八卦资讯聚合平台。我们专注于追踪<strong>网红明星塌房</strong>事件，为吃瓜群众提供最新、最快、最全的爆料内容。
                    </p>
                    <p>
                      我们的编辑团队由资深娱乐记者和网络观察员组成，通过多渠道交叉验证，确保每一条资讯的真实性和时效性。从直播间翻车到明星出轨，从虚假人设曝光到行业黑幕揭露，<strong className="text-foreground-900">51每日大赛</strong>始终站在吃瓜第一线。
                    </p>
                    <p>
                      截至目前，平台已累计发布超过<strong className="text-primary-500">1,800篇</strong>原创爆料内容，覆盖<strong className="text-primary-500">超过500位</strong>网红明星，月活跃用户突破百万。我们坚持<strong>每日更新</strong>，保持全天候的<strong>实时追踪</strong>能力，让每一位用户都不会错过任何一条有价值的吃瓜资讯。
                    </p>
                  </div>
                  <Link
                    to="/category/meiri-chigua"
                    className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    浏览最新吃瓜
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                <div className="lg:w-[360px] flex-shrink-0">
                  <div className="relative rounded-xl overflow-hidden h-64 md:h-80">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20interior%20with%20warm%20ambient%20lighting%2C%20editorial%20team%20workspace%20with%20large%20monitors%20displaying%20news%20content%2C%20professional%20media%20company%20atmosphere%2C%20trendy%20urban%20design%2C%20golden%20hour%20warm%20tones&width=600&height=800&seq=about-brand-visual-02&orientation=portrait"
                      alt="51每日大赛品牌形象"
                      title="51每日大赛编辑团队工作场景"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 px-3 py-1.5 bg-background-50/90 text-foreground-800 text-xs font-medium rounded-lg whitespace-nowrap">
                      平台认证
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-16">
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="text-3xl md:text-4xl font-black text-foreground-900">1,800+</p>
                  <p className="text-foreground-500 text-sm mt-1">累计爆料数</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="text-3xl md:text-4xl font-black text-foreground-900">500+</p>
                  <p className="text-foreground-500 text-sm mt-1">覆盖网红明星</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="text-3xl md:text-4xl font-black text-foreground-900">100万+</p>
                  <p className="text-foreground-500 text-sm mt-1">月活跃用户</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="text-3xl md:text-4xl font-black text-foreground-900">24/7</p>
                  <p className="text-foreground-500 text-sm mt-1">全天候追踪</p>
                </div>
              </div>

              {/* Reputation / Features */}
              <div className="mb-16">
                <h2 className="text-2xl md:text-3xl font-black text-foreground-900 mb-8">
                  平台口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                      <i className="ri-flashlight-line text-primary-500 text-lg"></i>
                    </div>
                    <h3 className="text-foreground-900 font-bold mb-2">极速追踪</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      重大事件发生后15分钟内上线报道，全行业最快的吃瓜响应速度。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className="ri-shield-check-line text-accent-500 text-lg"></i>
                    </div>
                    <h3 className="text-foreground-900 font-bold mb-2">交叉验证</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      多渠道来源交叉验证，对未证实信息明确标注，拒绝谣言传播。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center mb-4">
                      <i className="ri-eye-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="text-foreground-900 font-bold mb-2">深度视角</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      不仅报道事件本身，更深入分析背后的行业影响和社会意义。
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div id="contact" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-black text-foreground-900 mb-8">
                  联系我们
                </h2>
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-1">
                    <form
                      id="contact-form"
                      data-readdy-form="true"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">
                          您的称呼
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的称呼"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">
                          电子邮箱
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的电子邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">
                          留言内容
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                        <p className="text-foreground-400 text-xs mt-1 text-right">{formData.message.length}/500</p>
                      </div>

                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      {formStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3">
                          感谢您的留言！我们会尽快与您取得联系。
                        </div>
                      )}
                      {formStatus === 'error' && formError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                          {formError}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                        <i className="ri-send-plane-line"></i>
                      </button>
                    </form>
                  </div>

                  <div className="lg:w-72 flex-shrink-0 space-y-6">
                    <div className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-mail-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-900 text-sm font-medium">投稿邮箱</p>
                          <p className="text-foreground-500 text-xs">tips@51meiridasai.com</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                          <i className="ri-phone-line text-accent-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-900 text-sm font-medium">爆料热线</p>
                          <p className="text-foreground-500 text-xs">400-888-5151</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
                          <i className="ri-map-pin-line text-secondary-600"></i>
                        </div>
                        <div>
                          <p className="text-foreground-900 text-sm font-medium">办公地址</p>
                          <p className="text-foreground-500 text-xs">北京市朝阳区望京SOHO T3 12层</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-time-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-900 text-sm font-medium">工作时间</p>
                          <p className="text-foreground-500 text-xs">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full h-[350px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dynamic%20flowing%20crimson%20and%20gold%20waves%20with%20subtle%20geometric%20patterns%2C%20modern%20artistic%20style%2C%20energetic%20atmosphere%2C%20warm%20dramatic%20tones%2C%20professional%20yet%20bold%20aesthetic%2C%20cinematic%20composition&width=1800&height=550&seq=about-cta-tips-03&orientation=landscape"
            alt="51每日大赛 - 有料就爆"
            title="51每日大赛爆料投稿"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 to-black/25"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-white text-4xl md:text-5xl font-black mb-6 animate-fade-in-up">
                  有料就爆
                </h2>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-900 rounded-full font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  投稿爆料
                  <i className="ri-arrow-right-line"></i>
                </a>
                <p className="text-white/60 text-sm mt-12 max-w-xs leading-relaxed">
                  24小时审核机制，爆料人信息严格保密
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-4">
              <div className="bg-background-100 rounded-xl p-5 md:p-6">
                <h4 className="text-foreground-900 font-bold text-base mb-2">51每日大赛是什么网站？</h4>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  51每日大赛是一个专注于网红明星塌房事件和娱乐八卦资讯的聚合平台，致力于为吃瓜群众提供最新、最快、最全的爆料内容。我们每日更新，覆盖网红圈、娱乐圈、电竞圈等多个领域。
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 md:p-6">
                <h4 className="text-foreground-900 font-bold text-base mb-2">如何向51每日大赛投稿爆料？</h4>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  您可以通过本页面上方的联系表单提交线索，或直接发送邮件至 tips@51meiridasai.com。我们承诺对爆料人信息严格保密，编辑团队会在核实后第一时间发布报道。
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 md:p-6">
                <h4 className="text-foreground-900 font-bold text-base mb-2">51每日大赛的内容来源可靠吗？</h4>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  我们的内容团队通过多渠道交叉验证信息来源。对于尚未完全证实的信息，我们会在文章中明确标注"待确认"，让读者自行判断。我们坚决拒绝传播未经核实的谣言。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}